import { createCardBodySchema } from '@klopotun/contracts';
import { z } from 'zod';
import type { CardFormValues } from '../model/card-form-values.js';
import { isBlankOrWholeNumber, toCardBodyDraft } from './card-form-adapter.js';

type Complaint = {
  field: keyof CardFormValues;
  message: string;
};

const COMPLAINT_BY_BODY_FIELD: Record<string, Complaint> = {
  label: { field: 'label', message: 'Вкажіть назву картки' },
  last4: { field: 'last4', message: 'Останні цифри — рівно чотири цифри' },
  brand: { field: 'brand', message: 'Платіжна система не підходить' },
  expiresMonth: {
    field: 'expiresMonth',
    message: 'Місяць і рік вказують разом; місяць — від 1 до 12',
  },
  expiresYear: { field: 'expiresYear', message: 'Рік — від 2000 до 2100' },
  currency: { field: 'currency', message: 'Оберіть валюту' },
  color: { field: 'color', message: 'Колір не підходить' },
};

const FALLBACK_COMPLAINT: Complaint = {
  field: 'label',
  message: 'Дані форми не пройшли перевірку',
};

const EXPIRY_FIELDS: (keyof CardFormValues)[] = ['expiresMonth', 'expiresYear'];

const complaintFor = (path: PropertyKey[]): Complaint => {
  const [firstSegment] = path;

  if (typeof firstSegment !== 'string') {
    return FALLBACK_COMPLAINT;
  }

  return COMPLAINT_BY_BODY_FIELD[firstSegment] ?? FALLBACK_COMPLAINT;
};

const cardFormValuesSchema = z.object({
  label: z.string(),
  last4: z.string(),
  brand: z.string(),
  expiresMonth: z.string(),
  expiresYear: z.string(),
  currency: z.string(),
  color: z.string(),
});

const checkAgainstContract = (values: CardFormValues, ctx: z.RefinementCtx): void => {
  for (const field of EXPIRY_FIELDS) {
    if (!isBlankOrWholeNumber(values[field])) {
      ctx.addIssue({ code: 'custom', path: [field], message: 'Тут потрібне число' });
    }
  }

  const checked = createCardBodySchema.safeParse(toCardBodyDraft(values));

  if (checked.success) {
    return;
  }

  for (const issue of checked.error.issues) {
    const complaint = complaintFor(issue.path);

    ctx.addIssue({ code: 'custom', path: [complaint.field], message: complaint.message });
  }
};

export const cardFormSchema = cardFormValuesSchema.superRefine(checkAgainstContract);
